import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft } from 'lucide-react';
import { useSiteContent } from '../context/SiteContentContext';
import { usePageTitle } from '../hooks/usePageTitle';
import SEO from '../components/SEO';
import NotFoundPage from './NotFoundPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Valid legal page IDs
const VALID_LEGAL_PAGES = ['privacy-policy', 'terms-of-service', 'cookie-policy'];

// Trilingual translations
const translations = {
  bg: {
    backToHome: 'Обратно към начало',
    lastUpdated: 'Последна актуализация: '
  },
  en: {
    backToHome: 'Back to Home',
    lastUpdated: 'Last updated: '
  },
  de: {
    backToHome: 'Zurück zur Startseite',
    lastUpdated: 'Zuletzt aktualisiert: '
  }
};

export default function LegalPage() {
  const { pageId } = useParams();
  const [page, setPage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const { language, localizedPath } = useSiteContent();
  
  // Get translations
  const t = translations[language] || translations.en;
  
  // Set page title based on legal page type
  const titleMap = {
    'privacy-policy': 'privacy-policy',
    'terms-of-service': 'terms-of-service',
    'cookie-policy': 'cookie-policy'
  };
  usePageTitle(titleMap[pageId] || 'privacy-policy');

  useEffect(() => {
    // Check if pageId is valid before fetching
    if (!VALID_LEGAL_PAGES.includes(pageId)) {
      setNotFound(true);
      setIsLoading(false);
      return;
    }

    const fetchPage = async () => {
      try {
        const response = await axios.get(`${API}/legal/${pageId}`);
        if (response.data) {
          setPage(response.data);
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error fetching legal page:', error);
        setNotFound(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPage();
  }, [pageId]);

  if (isLoading) {
    return (
      <div className="pt-20 min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#C41E3A] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  // Show 404 page content without redirect (better for SEO)
  if (notFound || !page) {
    return <NotFoundPage />;
  }

  // Get localized content with fallback to English
  const title = page[`title_${language}`] || page.title_en;
  const content = page[`content_${language}`] || page.content_en;
  
  // Locale map for date formatting
  const localeMap = { bg: 'bg-BG', en: 'en-GB', de: 'de-DE' };
  const locale = localeMap[language] || 'en-GB';

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      <SEO page={pageId} />
      {/* Hero */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link to={localizedPath('/')} className="inline-flex items-center text-slate-500 hover:text-[#C41E3A] mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t.backToHome}
          </Link>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900">
            {title}
          </h1>
          {page.last_updated && (
            <p className="text-slate-500 mt-4 text-sm">
              {t.lastUpdated}
              {new Date(page.last_updated).toLocaleDateString(locale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </p>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div 
            className="prose prose-slate max-w-none prose-headings:text-slate-900 prose-p:text-slate-600 prose-li:text-slate-600 prose-a:text-[#C41E3A]"
            dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }}
          />
        </div>
      </section>
    </div>
  );
}
